type CleanupFn = () => void | Promise<void>;

const REGISTRY_KEY = "__dwimDevCleanupRegistry";
const TRACKED_BLOB_URLS_KEY = "__dwimDevTrackedBlobUrls";
const TRACKED_MEDIA_STREAMS_KEY = "__dwimDevTrackedMediaStreams";
const USER_OPERATION_DEPTH_KEY = "__dwimUserOperationDepth";
const PENDING_DEV_CLEANUP_KEY = "__dwimPendingDevCleanup";

type CleanupRegistry = {
  cleaners: Map<string, CleanupFn>;
  pagehideHookInstalled: boolean;
  generation: number;
};

type ImportMetaWithHot = ImportMeta & {
  hot?: { dispose(callback: () => void): void };
};

type NodeModuleWithHot = NodeModule & {
  hot?: { dispose(callback: () => void): void };
};

function isDevBrowser(): boolean {
  return process.env.NODE_ENV !== "production" && typeof window !== "undefined";
}

function readRegistry(): CleanupRegistry {
  const root = globalThis as typeof globalThis & {
    [REGISTRY_KEY]?: CleanupRegistry;
  };
  if (!root[REGISTRY_KEY]) {
    root[REGISTRY_KEY] = {
      cleaners: new Map(),
      pagehideHookInstalled: false,
      generation: 0,
    };
  }
  return root[REGISTRY_KEY];
}

function readStringSet(key: string): Set<string> {
  const root = globalThis as typeof globalThis & Record<string, Set<string> | undefined>;
  if (!root[key]) {
    root[key] = new Set();
  }
  return root[key] as Set<string>;
}

function readMediaStreamSet(): Set<MediaStream> {
  const root = globalThis as typeof globalThis & {
    [TRACKED_MEDIA_STREAMS_KEY]?: Set<MediaStream>;
  };
  if (!root[TRACKED_MEDIA_STREAMS_KEY]) {
    root[TRACKED_MEDIA_STREAMS_KEY] = new Set();
  }
  return root[TRACKED_MEDIA_STREAMS_KEY];
}

/** Register a dev-only teardown callback (replaced on duplicate name). */
export function registerDevCleanup(name: string, fn: CleanupFn): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  readRegistry().cleaners.set(name, fn);
}

/**
 * Register teardown for a resource module and hook module HMR dispose.
 * Use this from modules that own caches, workers, or other heap-heavy state.
 */
export function registerDevResourceCleanup(name: string, fn: CleanupFn): void {
  registerDevCleanup(name, fn);
  if (!isDevBrowser()) {
    return;
  }
  registerHmrDispose(() => {
    void runDevCleanups();
  });
}

export function getDevCleanupGeneration(): number {
  return readRegistry().generation;
}

function readUserOperationDepth(): number {
  const root = globalThis as typeof globalThis & { [USER_OPERATION_DEPTH_KEY]?: number };
  const depth = root[USER_OPERATION_DEPTH_KEY] ?? 0;
  return Number.isFinite(depth) && depth > 0 ? depth : 0;
}

function writePendingDevCleanup(pending: boolean): void {
  (globalThis as typeof globalThis & { [PENDING_DEV_CLEANUP_KEY]?: boolean })[
    PENDING_DEV_CLEANUP_KEY
  ] = pending;
}

function readPendingDevCleanup(): boolean {
  return (
    globalThis as typeof globalThis & { [PENDING_DEV_CLEANUP_KEY]?: boolean }
  )[PENDING_DEV_CLEANUP_KEY] === true;
}

/** True while Improve, Keep, or similar user flows must not be interrupted by HMR teardown. */
export function isUserOperationActive(): boolean {
  return readUserOperationDepth() > 0;
}

/** Mark the start of a user operation that must survive dev HMR cleanups. */
export function acquireUserOperation(): void {
  if (!isDevBrowser()) {
    return;
  }
  const root = globalThis as typeof globalThis & { [USER_OPERATION_DEPTH_KEY]?: number };
  root[USER_OPERATION_DEPTH_KEY] = readUserOperationDepth() + 1;
}

/** Mark the end of a user operation; runs any deferred dev cleanups when the last one ends. */
export function releaseUserOperation(): void {
  if (!isDevBrowser()) {
    return;
  }
  const root = globalThis as typeof globalThis & { [USER_OPERATION_DEPTH_KEY]?: number };
  const nextDepth = Math.max(0, readUserOperationDepth() - 1);
  root[USER_OPERATION_DEPTH_KEY] = nextDepth;
  if (nextDepth === 0 && readPendingDevCleanup()) {
    writePendingDevCleanup(false);
    void runDevCleanups();
  }
}

async function runDevCleanupsInner(): Promise<void> {
  const registry = readRegistry();
  registry.generation += 1;

  const cleaners = [...registry.cleaners.entries()].reverse();
  for (const [name, fn] of cleaners) {
    try {
      await fn();
    } catch (error) {
      console.warn(`[dev-cleanup] ${name} failed:`, error);
    }
  }
}

/** Run all registered dev teardown callbacks. */
export async function runDevCleanups(): Promise<void> {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  if (isUserOperationActive()) {
    writePendingDevCleanup(true);
    return;
  }

  await runDevCleanupsInner();
}

export function trackBlobUrl(url: string): void {
  if (process.env.NODE_ENV === "production" || !url.startsWith("blob:")) {
    return;
  }
  readStringSet(TRACKED_BLOB_URLS_KEY).add(url);
}

export function untrackBlobUrl(url: string): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  readStringSet(TRACKED_BLOB_URLS_KEY).delete(url);
}

function revokeTrackedBlobUrls(): void {
  const urls = readStringSet(TRACKED_BLOB_URLS_KEY);
  for (const url of urls) {
    URL.revokeObjectURL(url);
  }
  urls.clear();
}

export function trackMediaStream(stream: MediaStream): void {
  if (!isDevBrowser()) {
    return;
  }
  readMediaStreamSet().add(stream);
}

export function untrackMediaStream(stream: MediaStream): void {
  if (!isDevBrowser()) {
    return;
  }
  readMediaStreamSet().delete(stream);
}

function stopTrackedMediaStreams(): void {
  for (const stream of readMediaStreamSet()) {
    stream.getTracks().forEach((track) => track.stop());
  }
  readMediaStreamSet().clear();
}

function registerBuiltInDevCleanups(): void {
  registerDevCleanup("tracked-blob-urls", revokeTrackedBlobUrls);
  registerDevCleanup("tracked-media-streams", stopTrackedMediaStreams);
}

function registerHmrDispose(onDispose: () => void): void {
  const moduleHot = (typeof module !== "undefined"
    ? (module as NodeModuleWithHot).hot
    : undefined);
  moduleHot?.dispose(onDispose);

  const importMetaHot = (import.meta as ImportMetaWithHot).hot;
  importMetaHot?.dispose(onDispose);
}

/** Hook HMR dispose and pagehide to run registered cleanups. */
export function installDevCleanupHooks(): void {
  if (!isDevBrowser()) {
    return;
  }

  registerBuiltInDevCleanups();

  const onDispose = () => {
    void runDevCleanups();
  };

  registerHmrDispose(onDispose);

  const registry = readRegistry();
  if (!registry.pagehideHookInstalled) {
    registry.pagehideHookInstalled = true;
    window.addEventListener("pagehide", onDispose);
  }
}

if (isDevBrowser()) {
  installDevCleanupHooks();
}
